import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/shared/components/ui/Button';
import type { Signal, SignalWithRelatedIssuesCount } from '@momentum/types';
import type { EmployeeInfo, SignalFilters } from '../constants';
import { useFilteredSignals } from './hooks/useFilteredSignals';
import { SignalGrid } from './components/SignalGrid';
import { SignalEmptyState } from './components/SignalEmptyState';
import { FilteredViewBanner } from './components/FilteredViewBanner';

interface SignalContentProps {
  allSignals: SignalWithRelatedIssuesCount[];
  filters: SignalFilters;
  isFilteredView: boolean;
  currentUserId: string;
  employeeMap: Map<string, EmployeeInfo>;
  onSignalClick: (signal: Signal) => void;
  onToggleActive: (signal: Signal, isActive: boolean) => void;
  togglingSignalId: string | null;
  onAddSignal: () => void;
}

export const SignalContent = ({
  allSignals,
  filters,
  isFilteredView,
  currentUserId,
  employeeMap,
  onSignalClick,
  onToggleActive,
  togglingSignalId,
  onAddSignal,
}: SignalContentProps) => {
  const { t } = useTranslation('knowledge');
  const navigate = useNavigate();
  const signals = useFilteredSignals(allSignals, filters, currentUserId);

  if (isFilteredView && signals.length === 0) {
    return (
      <>
        <FilteredViewBanner signalCount={0} />
        <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
          <p className="text-muted-foreground">
            {t('noAccessibleSignalsForIds')}
          </p>
          <Button
            variant="link"
            onClick={() =>
              navigate({
                to: '/knowledge',
                search: { signalIds: undefined },
              })
            }
          >
            {t('viewAllSignals')}
          </Button>
        </div>
      </>
    );
  }

  if (signals.length === 0 && allSignals.length === 0 && !isFilteredView) {
    return <SignalEmptyState type="no-signals" onAddSignal={onAddSignal} />;
  }

  if (signals.length === 0 && allSignals.length > 0 && !isFilteredView) {
    return <SignalEmptyState type="no-results" onAddSignal={onAddSignal} />;
  }

  return (
    <>
      {isFilteredView && <FilteredViewBanner signalCount={signals.length} />}
      <SignalGrid
        signals={signals}
        currentUserId={currentUserId}
        employeeMap={employeeMap}
        onSignalClick={onSignalClick}
        onToggleActive={onToggleActive}
        togglingSignalId={togglingSignalId}
      />
    </>
  );
};
